/**
 * LMS Real-time Notifications Handler
 * Listens to WebSocket events and displays toast notifications
 */

class LmsNotifications {
    constructor() {
        this.userId = null;
        this.courseIds = [];
        this.initialized = false;
    }

    /**
     * Initialize notifications for a user
     * @param {number} userId - The authenticated user's ID
     * @param {array} courseIds - Array of course site IDs the user is enrolled in
     */
    init(userId, courseIds = []) {
        if (this.initialized) {
            console.warn('LMS Notifications already initialized');
            return;
        }

        if (!window.Echo) {
            console.error('Laravel Echo not initialized');
            return;
        }

        this.userId = userId;
        this.courseIds = courseIds;
        this.initialized = true;

        this.subscribeToUserChannel();
        this.subscribeToCourseChannels();

        console.log('LMS Notifications initialized for user:', userId);
    }

    /**
     * Subscribe to user-specific private channel
     */
    subscribeToUserChannel() {
        if (!this.userId) return;

        window.Echo.private(`user.${this.userId}`)
            .listen('.grade.posted', (event) => {
                this.showNotification('Grade Posted', event.message, 'success', {
                    url: `/lms/courses/${event.course_site_id}/grades`,
                    icon: 'fa-star'
                });
            })
            .listen('.submission.graded', (event) => {
                this.showNotification('Submission Graded', event.message, 'info', {
                    url: `/lms/courses/${event.course_site_id}/assignments/${event.assignment_id}`,
                    icon: 'fa-check-circle'
                });
            });

        console.log(`Subscribed to user.${this.userId} channel`);
    }

    /**
     * Subscribe to course-specific channels
     */
    subscribeToCourseChannels() {
        this.courseIds.forEach(courseId => {
            window.Echo.private(`course.${courseId}`)
                .listen('.announcement.posted', (event) => {
                    this.showNotification('New Announcement', event.message, 'info', {
                        url: `/lms/courses/${event.course_site_id}/announcements/${event.announcement_id}`,
                        icon: 'fa-bullhorn'
                    });
                })
                .listen('.assignment.due', (event) => {
                    this.showNotification('Assignment Reminder', event.message, 'warning', {
                        url: `/lms/courses/${event.course_site_id}/assignments/${event.assignment_id}`,
                        icon: 'fa-clock'
                    });
                })
                .listen('.quiz.available', (event) => {
                    this.showNotification('Quiz Available', event.message, 'primary', {
                        url: `/lms/courses/${event.course_site_id}/quizzes/${event.quiz_id}`,
                        icon: 'fa-question-circle'
                    });
                })
                .listen('.conference.starting', (event) => {
                    this.showNotification('Video Conference Starting', event.message, 'success', {
                        url: event.join_url,
                        external: true,
                        icon: 'fa-video'
                    });
                });

            console.log(`Subscribed to course.${courseId} channel`);
        });
    }

    /**
     * Show a toast notification
     * @param {string} title - Notification title
     * @param {string} message - Notification message
     * @param {string} type - Type: success, warning, danger, info, primary
     * @param {object} options - Additional options (url, icon, external)
     */
    showNotification(title, message, type = 'info', options = {}) {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `toast show align-items-center text-white bg-${type} border-0`;
        notification.setAttribute('role', 'alert');
        notification.setAttribute('aria-live', 'assertive');
        notification.setAttribute('aria-atomic', 'true');

        const icon = options.icon || this.getIconForType(type);

        notification.innerHTML = `
            <div class="d-flex">
                <div class="toast-body">
                    <div class="d-flex align-items-center mb-1">
                        <i class="fas ${icon} me-2"></i>
                        <strong>${title}</strong>
                    </div>
                    <div>${message}</div>
                    ${options.url ? `
                        <a href="${options.url}"
                           class="btn btn-sm btn-light mt-2"
                           ${options.external ? 'target="_blank"' : ''}>
                            View Details
                        </a>
                    ` : ''}
                </div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto"
                        data-bs-dismiss="toast" aria-label="Close"></button>
            </div>
        `;

        // Add to container or create one
        let container = document.getElementById('notification-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'notification-container';
            container.className = 'toast-container position-fixed top-0 end-0 p-3';
            container.style.zIndex = '9999';
            document.body.appendChild(container);
        }

        container.appendChild(notification);

        // Play notification sound
        this.playSound();

        // Auto-dismiss after 8 seconds
        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => notification.remove(), 300);
        }, 8000);

        // Handle close button
        notification.querySelector('.btn-close').addEventListener('click', () => {
            notification.classList.remove('show');
            setTimeout(() => notification.remove(), 300);
        });
    }

    /**
     * Get icon for notification type
     */
    getIconForType(type) {
        const icons = {
            success: 'fa-check-circle',
            warning: 'fa-exclamation-triangle',
            danger: 'fa-times-circle',
            info: 'fa-info-circle',
            primary: 'fa-bell'
        };
        return icons[type] || 'fa-bell';
    }

    /**
     * Play notification sound
     */
    playSound() {
        try {
            // Use Web Audio API for a simple notification sound
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.value = 0.1;

            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.15);
        } catch (e) {
            // Audio not supported or blocked
        }
    }

    /**
     * Add a course to listen to
     * @param {number} courseId - Course site ID to subscribe
     */
    addCourse(courseId) {
        if (this.courseIds.includes(courseId)) return;

        this.courseIds.push(courseId);
        this.subscribeToCourseChannels([courseId]);
    }

    /**
     * Remove a course subscription
     * @param {number} courseId - Course site ID to unsubscribe
     */
    removeCourse(courseId) {
        const index = this.courseIds.indexOf(courseId);
        if (index > -1) {
            this.courseIds.splice(index, 1);
            window.Echo.leave(`course.${courseId}`);
        }
    }

    /**
     * Cleanup all subscriptions
     */
    destroy() {
        if (this.userId) {
            window.Echo.leave(`user.${this.userId}`);
        }
        this.courseIds.forEach(courseId => {
            window.Echo.leave(`course.${courseId}`);
        });
        this.initialized = false;
    }
}

// Create global instance
window.LmsNotifications = new LmsNotifications();

// Export for module usage
export default window.LmsNotifications;
